angular.module('bizmetryApp').controller('AgentConfigDialogController', [
  '$scope', '$rootScope', '$mdDialog', '$mdToast', '$q', '$timeout',
  'agent', 'ReferenceDataService', 'ConfigurationService', 'TelemetryAgentService', 'dialogService', 'PlatformInfoService', 'UserService',
  function (
    $scope, $rootScope, $mdDialog, $mdToast, $q, $timeout,
    agent, ReferenceDataService, ConfigurationService, TelemetryAgentService, dialogService, PlatformInfoService, UserService
  ) {
    console.log('MEMO received Agent=', agent);

    // ─────────────────────────────────────────────
    // 🔹 Copias de agente
    // ─────────────────────────────────────────────
    const receivedAgent = angular.copy(agent);
    // The API (both GET /agents/{id} and GET /agents/search) serves this
    // field as "network_settings" (snake_case) — every OTHER field on this
    // DTO is plain camelCase, this is the one exception (see
    // TelemetryAgentService.java's own @JsonProperty("network_settings")
    // comment). Without this normalization, receivedAgent.networkSettings
    // is always undefined, so the [NETWORK SETTINGS] seed block below
    // always treats it as "brand new / never configured" and silently
    // reseeds pure defaults over whatever was actually persisted — every
    // dialog open looked like the agent had never had these fields set,
    // and every save appeared to "not stick" because the next open reset
    // right back to defaults.
    if (receivedAgent.networkSettings == null && receivedAgent.network_settings != null) {
      receivedAgent.networkSettings = receivedAgent.network_settings;
    }
    $scope.originalAgent = angular.copy(receivedAgent);
    $scope.agent = angular.copy(receivedAgent);
    $scope.isFormValid = true;
    $scope.urlErrorMessage = '';

    // 🔹 Dynamic Capacity / HPA temporarily hidden from the UI — a Dynamic
    // agent's local per-pod disk buffer (queued-but-not-yet-uploaded data)
    // has no drain step today, so an HPA scale-down (or a manual replica
    // decrease) can silently strand unprocessed data on the removed pod's
    // PVC. Backend support (OSConfig fields, KubernetesYAMLGenerator's HPA
    // generation) is left intact — flip this back to true once the storage
    // design is revisited, rather than ripping the feature back out.
    $scope.hpaEnabled = false;

    // Pre-existing agents saved before this field existed have no
    // networkTopology at all — default to DIFFERENT_CLUSTER (the only
    // behavior that existed until now), same convention as the Capacity
    // tab's own osConfig defaults below.
    if ($scope.agent.securityConfig && $scope.agent.securityConfig.internal
        && !$scope.agent.securityConfig.internal.networkTopology) {
      $scope.agent.securityConfig.internal.networkTopology = 'DIFFERENT_CLUSTER';
      $scope.originalAgent.securityConfig.internal.networkTopology = 'DIFFERENT_CLUSTER';
    }

    // 🔹 Regex para validar dirección externa
    $scope.gatewayPattern = /^(?:[\w.-]+|\d{1,3}(?:\.\d{1,3}){3})(?::\d+)?(?:\/[^\s]*)?$/;

    // ─────────────────────────────────────────────
    // 🔹 Carga de límites configurables desde cache
    // ─────────────────────────────────────────────
    const getLimit = (key, fallback) => ConfigurationService.getFromCache(key) || fallback;
    const limitsRaw = {
      // Metrics Heap Buffer Capacity (label renamed from "Metrics Disk
      // Buffer Size") is edited in MB, not KB — same unit switch as Metrics
      // Memory Buffer Size below (see agentMaxDiskBufferSize's own comment
      // in TelemetryAgent.java). 8192 (8GB).
      maxDiskBufferMb: getLimit('frontend.agent.max_disk_buffer_mb', 8192),
      // Metrics Frame Buffer Capacity (label renamed from "Metrics Disk Max
      // Frames") — entry-count cap on the agent's own on-disk metrics queue,
      // independent of the byte-capacity cap above (see
      // agentMetricsDiskMaxFrames's own comment in TelemetryAgent.java).
      // Was a hardcoded, unconfigurable 32767 until this field existed. Both
      // bounds come from bizmetry-shared's ConfigMap (unlike Metrics Disk
      // Buffer Size above, whose floor is a plain hardcoded 16 in the markup).
      minMetricsDiskMaxFrames: getLimit('frontend.agent.min_metrics_disk_max_frames', 16384),
      maxMetricsDiskMaxFrames: getLimit('frontend.agent.max_metrics_disk_max_frames', 1048576),
      // Metrics Memory Buffer Size is edited in MB, not KB (the backend's
      // agentMaxMemBufferSize field switched units to match — see
      // KubernetesYAMLGenerator's own memory-computation comment) — 4096
      // (4GB) matches AGENT_MAX_MEM_ALLOC's own ceiling headroom.
      maxMemBufferMb: getLimit('frontend.agent.max_mem_buffer_mb', 4096),
      maxLogBufferKb: getLimit('frontend.agent.max_log_buffer_kb', 131072),
      maxLogBatchSizeKb: getLimit('frontend.agent.max_log_batch_size_kb', 10240),
      // SAF Forwarder pool (Tuning tab) — same shape as
      // the interceptor's own Self-Tuning limits (Initial/Max Transmit
      // Worker Count, Initial/Max Sync Batch Size): one shared floor/ceiling
      // pair for both the Initial and Max slider of each field (Max can
      // never exceed the ceiling, Initial can never exceed Max — see the
      // four clamp functions below), plus a default that seeds Initial
      // Count/Initial Batch Size when the agent doesn't have a value yet
      // (a brand-new agent, or one created before these fields existed).
      minSafForwarderCount: getLimit('frontend.agent.min_saf_forwarder_count', 1),
      maxSafForwarderCount: getLimit('frontend.agent.max_saf_forwarder_count', 16),
      defaultSafForwarderCount: getLimit('frontend.agent.default_saf_forwarder_count', 4),
      minSafUploadBatchSizeFrames: getLimit('frontend.agent.min_saf_upload_batch_size_frames', 64),
      maxSafUploadBatchSizeFrames: getLimit('frontend.agent.max_saf_upload_batch_size_frames', 10240),
      defaultSafUploadBatchSizeFrames: getLimit('frontend.agent.default_saf_upload_batch_size_frames', 256),
      // SAF Forwarder auto-scaling pressure thresholds — same shared
      // floor/ceiling pair shape as the rest of the SAF fields above, plus
      // defaults for scale-down/scale-up (20%/80%) that seed a brand-new
      // agent (see the null-coalescing block below). Config + provisioning
      // only in this pass — no auto-scaler reads these yet.
      minSafForwarderScaleThresholdPct: getLimit('frontend.agent.min_saf_forwarder_scale_threshold_pct', 1),
      maxSafForwarderScaleThresholdPct: getLimit('frontend.agent.max_saf_forwarder_scale_threshold_pct', 100),
      defaultSafForwarderScaleDownThresholdPct: getLimit('frontend.agent.default_saf_forwarder_scale_down_threshold_pct', 20),
      defaultSafForwarderScaleUpThresholdPct: getLimit('frontend.agent.default_saf_forwarder_scale_up_threshold_pct', 80),
      // Network Settings (Tuning tab) — same numbers the interceptor's own
      // Connect/Read Timeout sliders used before this moved here (there was
      // no real ConfigMap backing them either, just these same hardcoded
      // fallbacks under a different key prefix).
      minNetworkConnectTimeoutSeconds: getLimit('frontend.agent.min_network_connect_timeout_seconds', 1),
      maxNetworkConnectTimeoutSeconds: getLimit('frontend.agent.max_network_connect_timeout_seconds', 60),
      defaultNetworkConnectTimeoutSeconds: getLimit('frontend.agent.default_network_connect_timeout_seconds', 5),
      minNetworkReadTimeoutSeconds: getLimit('frontend.agent.min_network_read_timeout_seconds', 1),
      maxNetworkReadTimeoutSeconds: getLimit('frontend.agent.max_network_read_timeout_seconds', 60),
      defaultNetworkReadTimeoutSeconds: getLimit('frontend.agent.default_network_read_timeout_seconds', 10),
      minMaxPayloadSizeMb: getLimit('frontend.agent.min_max_payload_size_mb', 1),
      maxMaxPayloadSizeMb: getLimit('frontend.agent.max_max_payload_size_mb', 32),
      defaultMaxPayloadSizeMb: getLimit('frontend.agent.default_max_payload_size_mb', 2),
      // Pull Data Batch Size — records per agent call in a bulk pull (not KB/MB).
      minBulkFetchBatchSize: getLimit('frontend.agent.min_bulk_fetch_batch_size', 64),
      maxBulkFetchBatchSize: getLimit('frontend.agent.max_bulk_fetch_batch_size', 4096),
      defaultBulkFetchBatchSize: getLimit('frontend.agent.default_bulk_fetch_batch_size', 1024),
      // agentSyncInterval is in SECONDS (the agent schedules its sync — and each SAF
      // forwarder its upload — once per interval). 3600 (1 h), same as the shared config;
      // the old 180000 fallback read like milliseconds (50 h in seconds).
      maxSyncInterval: getLimit('frontend.agent.max_sync_interval', 3600),
      // 🔹 [MONITORING] Límites
      maxLatency: getLimit('frontend.agent.max_latency_ms', 10000),
      maxAggregationInterval: getLimit('frontend.agent.max_aggregation_interval_min', 60),
      // Porcentaje mínimo (0-100) del rango total que deben mantener separados los handles CLEAR y SET.
      thresholdMinRangePct: getLimit('frontend.agent.threshold_min_range_pct', 20),
      // 🔹 [AUTOSCALING] Límites y defaults numéricos — nada hardcodeado acá,
      // todo viene de bizmetry-shared-services (central-config.yaml).
      minReplicas: getLimit('frontend.agent.autoscaling.min-replicas', 1),
      maxReplicas: getLimit('frontend.agent.autoscaling.max-replicas', 16),
      defaultFixedReplicas: getLimit('frontend.agent.autoscaling.default-fixed-replicas', 3),
      defaultDynamicInitialReplicas: getLimit('frontend.agent.autoscaling.default-dynamic-initial-replicas', 2),
      defaultDynamicMaxReplicas: getLimit('frontend.agent.autoscaling.default-dynamic-max-replicas', 6),
      minThresholdPercent: getLimit('frontend.agent.autoscaling.min-threshold-percent', 0),
      maxThresholdPercent: getLimit('frontend.agent.autoscaling.max-threshold-percent', 100),
      defaultCpuThresholdPercent: getLimit('frontend.agent.autoscaling.default-cpu-threshold-percent', 75),
      defaultMemoryThresholdPercent: getLimit('frontend.agent.autoscaling.default-memory-threshold-percent', 80),
      minThresholdDuration: getLimit('frontend.agent.autoscaling.min-threshold-duration-minutes', 1),
      maxThresholdDuration: getLimit('frontend.agent.autoscaling.max-threshold-duration-minutes', 60),
      defaultThresholdDuration: getLimit('frontend.agent.autoscaling.default-threshold-duration-minutes', 5),
      minCpuAssignment: getLimit('frontend.agent.autoscaling.min-cpu-assignment-floor-millicores', 500),
      maxCpuAssignment: getLimit('frontend.agent.autoscaling.max-cpu-assignment-ceiling-millicores', 16000),
      defaultMinCpuAssignment: getLimit('frontend.agent.autoscaling.default-min-cpu-assignment-millicores', 500),
      defaultMaxCpuAssignment: getLimit('frontend.agent.autoscaling.default-max-cpu-assignment-millicores', 2000)
    };
    $scope.limits = Object.fromEntries(
      Object.entries(limitsRaw).map(([key, value]) => [key, Number(value)])
    );
    // Capacity mode default es un string, no pasa por la coerción numérica de arriba.
    $scope.limits.defaultCapacityMode = ConfigurationService.getFromCache('frontend.agent.autoscaling.default-capacity-mode') || 'FIXED';

    // 🔹 [MONITORING] Steps derivados para los sliders
    $scope.limits.stepLatency = Math.max(1, Math.floor($scope.limits.maxLatency * 0.01));
    $scope.limits.stepAggregationInterval = Math.max(1, Math.floor($scope.limits.maxAggregationInterval * 0.01));
    $scope.limits.stepDiskBufferMb = Math.max(1, Math.floor($scope.limits.maxDiskBufferMb * 0.01));
    $scope.limits.stepMetricsDiskMaxFrames = Math.max(1, Math.floor(
      ($scope.limits.maxMetricsDiskMaxFrames - $scope.limits.minMetricsDiskMaxFrames) * 0.01));
    $scope.limits.stepMemBufferMb = Math.max(1, Math.floor($scope.limits.maxMemBufferMb * 0.01));
    $scope.limits.stepLogBufferKb = Math.max(1, Math.floor($scope.limits.maxLogBufferKb * 0.01));
    $scope.limits.stepLogBatchSizeKb = Math.max(1, Math.floor($scope.limits.maxLogBatchSizeKb * 0.01));
    $scope.limits.stepSafUploadBatchSizeFrames = Math.max(1, Math.floor(
      ($scope.limits.maxSafUploadBatchSizeFrames - $scope.limits.minSafUploadBatchSizeFrames) * 0.01));
    $scope.limits.stepSafForwarderScaleThresholdPct = Math.max(1, Math.floor(
      ($scope.limits.maxSafForwarderScaleThresholdPct - $scope.limits.minSafForwarderScaleThresholdPct) * 0.01));
    // Fixed 5 s step from the 10 s minimum (10, 15, 20, 30…) — 1% of the max (36 s with
    // the 3600 s max) only allowed 10, 46, 82… and never the usual 20 s / 30 s values.
    $scope.limits.stepSyncInterval = 5;

    // Clamp thresholdMinRangePct a [0, 100]
    $scope.limits.thresholdMinRangePct = Math.min(100, Math.max(0, $scope.limits.thresholdMinRangePct));
    $scope.limits.thresholdMinRangeCpu = $scope.limits.thresholdMinRangePct;
    $scope.limits.thresholdMinRangeMemory = $scope.limits.thresholdMinRangePct;
    $scope.limits.thresholdMinRangePressure = $scope.limits.thresholdMinRangePct;
    $scope.limits.thresholdMinRangeLatency = Math.max(
      $scope.limits.stepLatency,
      Math.ceil($scope.limits.maxLatency * $scope.limits.thresholdMinRangePct / 100)
    );

    // ─────────────────────────────────────────────
    // 🔹 Reference data (OS types & ingress types)
    // ─────────────────────────────────────────────
    ReferenceDataService.getReferenceData('AGENT_OS_TYPE')
      .then(function (response) {
        $scope.agentOsTypes = response.data || [];
        console.log('✅ Agent OS types loaded:', $scope.agentOsTypes);
      })
      .catch(function (error) {
        console.error('❌ Error al cargar los tipos de OS:', error);
        $scope.agentOsTypes = [];
      });

    ReferenceDataService.getReferenceData('AGENT_INGRESS_TYPE')
      .then(function (response) {
        $scope.agentIngressTypes = response.data || [];
      })
      .catch(function (error) {
        $scope.agentIngressTypes = [];
      });

    // ─────────────────────────────────────────────
    // 🔹 [SAF FORWARDER] Seed Initial Count/Initial Batch Size when the
    // agent doesn't have a value yet — a brand-new agent being configured
    // before its first save, or one created before these 4 fields existed
    // (their create-time defaulting in TelemetryAgentService only runs for
    // agents created AFTER this feature shipped). Max Count/Max Batch Size
    // default to the slider's own ceiling — a full-range default is a safe
    // choice when there's no narrower signal to seed from.
    // ─────────────────────────────────────────────
    if ($scope.agent.agentSafForwarderInitialCount == null) {
      $scope.agent.agentSafForwarderInitialCount = $scope.limits.defaultSafForwarderCount;
    }
    if ($scope.agent.agentSafForwarderMaxCount == null) {
      $scope.agent.agentSafForwarderMaxCount = $scope.limits.maxSafForwarderCount;
    }
    if ($scope.agent.agentSafUploadInitialBatchSizeFrames == null) {
      $scope.agent.agentSafUploadInitialBatchSizeFrames = $scope.limits.defaultSafUploadBatchSizeFrames;
    }
    if ($scope.agent.agentSafUploadMaxBatchSizeFrames == null) {
      $scope.agent.agentSafUploadMaxBatchSizeFrames = $scope.limits.maxSafUploadBatchSizeFrames;
    }
    if ($scope.agent.agentSafForwarderScaleDownPressureThresholdPct == null) {
      $scope.agent.agentSafForwarderScaleDownPressureThresholdPct = $scope.limits.defaultSafForwarderScaleDownThresholdPct;
    }
    if ($scope.agent.agentSafForwarderScaleUpPressureThresholdPct == null) {
      $scope.agent.agentSafForwarderScaleUpPressureThresholdPct = $scope.limits.defaultSafForwarderScaleUpThresholdPct;
    }

    // ─────────────────────────────────────────────
    // 🔹 [NETWORK SETTINGS] Seed defaults when the agent doesn't have a
    // networkSettings object yet (a brand-new agent, or one created before
    // this feature existed) — same "seed missing numeric fields, default
    // proxy to disabled/empty" shape as the interceptor's own old
    // _defaultProxyConfig(). proxyPassword is forced blank on BOTH agent and
    // originalAgent (not just displayed blank) so hasChanges() only flags it
    // as changed when the user actually types a new one — leaving it blank
    // on save means "keep the current password" (see TelemetryAgentService's
    // own preserve-if-blank handling).
    // ─────────────────────────────────────────────
    if (!$scope.agent.networkSettings) { $scope.agent.networkSettings = {}; }
    if ($scope.agent.networkSettings.connectTimeoutSeconds == null) {
      $scope.agent.networkSettings.connectTimeoutSeconds = $scope.limits.defaultNetworkConnectTimeoutSeconds;
    }
    if ($scope.agent.agentMaxPayloadSizeMb == null) {
      $scope.agent.agentMaxPayloadSizeMb = $scope.limits.defaultMaxPayloadSizeMb;
      $scope.originalAgent.agentMaxPayloadSizeMb = $scope.agent.agentMaxPayloadSizeMb;
    }
    if ($scope.agent.agentBulkFetchBatchSize == null) {
      $scope.agent.agentBulkFetchBatchSize = $scope.limits.defaultBulkFetchBatchSize;
      $scope.originalAgent.agentBulkFetchBatchSize = $scope.agent.agentBulkFetchBatchSize;
    }
    if ($scope.agent.networkSettings.readTimeoutSeconds == null) {
      $scope.agent.networkSettings.readTimeoutSeconds = $scope.limits.defaultNetworkReadTimeoutSeconds;
    }
    $scope.agent.networkSettings.proxyEnabled = !!$scope.agent.networkSettings.proxyEnabled;
    // "HTTP" is the only protocol the interceptor SDK can actually use to
    // CONNECT to the proxy today (java.net.http.HttpClient has no built-in
    // TLS-to-proxy support) — "HTTPS" is shown in the picker as a disabled
    // "coming soon" option, so any existing/blank value normalizes to HTTP.
    $scope.agent.networkSettings.proxyScheme = $scope.agent.networkSettings.proxyScheme || 'HTTP';
    $scope.agent.networkSettings.proxyHost = $scope.agent.networkSettings.proxyHost || '';
    $scope.agent.networkSettings.proxyUsername = $scope.agent.networkSettings.proxyUsername || '';
    $scope.agent.networkSettings.proxyPassword = '';
    // Sync originalAgent so hasChanges() doesn't false-positive on these
    // normalized defaults (same reasoning as osConfig's own sync below) —
    // without this, a freshly-opened dialog whose agent predates one of
    // these fields (or has proxyHost/proxyUsername stored as null instead
    // of '') would show Save as enabled with no actual user edit yet.
    $scope.originalAgent.networkSettings = angular.copy($scope.agent.networkSettings);

    // ─────────────────────────────────────────────
    // 🔹 Inicializar osConfig
    // ─────────────────────────────────────────────
    if (!$scope.agent.osConfig) {
      $scope.agent.osConfig = {
        agentOsTypeId: null,
        agentIngressTypeId: null,
        registryURL: '',
        enabled: false,
        registryProject: '',
        registryUserName: '',
        registryPassword: '',
        // AutoScaling — defaults recomendados, leídos de bizmetry-shared-services
        // (nada hardcodeado acá); el backend re-aplica los mismos valores
        // (config-driven, su propio ConfigMap) al crear si por algún motivo no
        // llegan seteados en el request.
        capacityMode: $scope.limits.defaultCapacityMode,
        fixedReplicas: $scope.limits.defaultFixedReplicas,
        dynamicInitialReplicas: $scope.limits.defaultDynamicInitialReplicas,
        dynamicMaxReplicas: $scope.limits.defaultDynamicMaxReplicas,
        cpuThresholdPercent: $scope.limits.defaultCpuThresholdPercent,
        memoryThresholdPercent: $scope.limits.defaultMemoryThresholdPercent,
        thresholdDurationMinutes: $scope.limits.defaultThresholdDuration,
        minCpuAssignment: $scope.limits.defaultMinCpuAssignment,
        maxCpuAssignment: $scope.limits.defaultMaxCpuAssignment
      };
    } else {
      $scope.agent.osConfig = {
        enabled: $scope.agent.osConfig.enabled || false,
        agentIngressTypeId: $scope.agent.osConfig.agentIngressTypeId || null,
        agentOsTypeId: $scope.agent.osConfig.agentOsTypeId || null,
        registryURL: $scope.agent.osConfig.registryURL || '',
        registryProject: $scope.agent.osConfig.registryProject || '',
        registryUserName: $scope.agent.osConfig.registryUserName || '',
        registryPassword: $scope.agent.osConfig.registryPassword || '',
        capacityMode: $scope.agent.osConfig.capacityMode || $scope.limits.defaultCapacityMode,
        fixedReplicas: $scope.agent.osConfig.fixedReplicas || $scope.limits.defaultFixedReplicas,
        dynamicInitialReplicas: $scope.agent.osConfig.dynamicInitialReplicas
          || $scope.limits.defaultDynamicInitialReplicas,
        dynamicMaxReplicas: $scope.agent.osConfig.dynamicMaxReplicas || $scope.limits.defaultDynamicMaxReplicas,
        cpuThresholdPercent: $scope.agent.osConfig.cpuThresholdPercent != null
          ? $scope.agent.osConfig.cpuThresholdPercent : $scope.limits.defaultCpuThresholdPercent,
        memoryThresholdPercent: $scope.agent.osConfig.memoryThresholdPercent != null
          ? $scope.agent.osConfig.memoryThresholdPercent : $scope.limits.defaultMemoryThresholdPercent,
        thresholdDurationMinutes: $scope.agent.osConfig.thresholdDurationMinutes != null
          ? $scope.agent.osConfig.thresholdDurationMinutes : $scope.limits.defaultThresholdDuration,
        minCpuAssignment: $scope.agent.osConfig.minCpuAssignment != null
          ? $scope.agent.osConfig.minCpuAssignment : $scope.limits.defaultMinCpuAssignment,
        maxCpuAssignment: $scope.agent.osConfig.maxCpuAssignment != null
          ? $scope.agent.osConfig.maxCpuAssignment : $scope.limits.defaultMaxCpuAssignment
      };
    }
    // Sync originalAgent so hasChanges() doesn't fire on normalized defaults
    $scope.originalAgent.osConfig = angular.copy($scope.agent.osConfig);
    console.log('✅ Agent OS Config initialized:', $scope.agent.osConfig);

    // ─────────────────────────────────────────────
    // 🔹 [ALERTING] Default configuration
    //    alertCPUUsage / alertMemoryUsage / alertLatency eliminados:
    //    esa configuración vive ahora en monitoringConfig.<metric>.alertingEnabled
    // ─────────────────────────────────────────────
    const DEFAULT_ALERT_CONFIGURATION = {
      alertingEnabled: false,
      alertEnableEmail: false,
      alertEnableSMS: false,
      alertUsers: []
    };

    const DEFAULT_MONITORING = {
      cpu: { enabled: true, alertingEnabled: true, thresholdClear: 60, thresholdSet: 95, setTime: 5, resetTime: 5 },
      memory: { enabled: true, alertingEnabled: true, thresholdClear: 50, thresholdSet: 90, setTime: 5, resetTime: 5 },
      latency: { enabled: true, alertingEnabled: true, thresholdClear: 5000, thresholdSet: 10000, setTime: 5, resetTime: 5 },
      pressure: { enabled: true, alertingEnabled: true, thresholdClear: 50, thresholdSet: 80, setTime: 5, resetTime: 5 },
      alertConfiguration: angular.copy(DEFAULT_ALERT_CONFIGURATION)
    };

    /**
     * Transforma la estructura anidada de la API al modelo plano del formulario.
     * Acepta tanto la estructura API { set: { threshold, time }, reset: { threshold, time } }
     * como el modelo plano ya inicializado (idempotente).
     */
    function _mapMonitoringToUI(raw) {
      var result = {};
      ['cpu', 'memory', 'latency', 'pressure'].forEach(function (metric) {
        var src = (raw && raw[metric]) || {};
        var def = DEFAULT_MONITORING[metric];
        var setBlock = src.set || {};
        var resetBlock = src.reset || {};
        result[metric] = {
          enabled: src.enabled != null ? src.enabled : def.enabled,
          alertingEnabled: src.alertingEnabled != null ? src.alertingEnabled : def.alertingEnabled,
          thresholdSet: setBlock.threshold != null ? setBlock.threshold : (src.thresholdSet != null ? src.thresholdSet : def.thresholdSet),
          thresholdClear: resetBlock.threshold != null ? resetBlock.threshold : (src.thresholdClear != null ? src.thresholdClear : def.thresholdClear),
          setTime: setBlock.time != null ? setBlock.time : (src.setTime != null ? src.setTime : def.setTime),
          resetTime: resetBlock.time != null ? resetBlock.time : (src.resetTime != null ? src.resetTime : def.resetTime)
        };
      });

      // Mapear alertConfiguration desde la API (o desde el modelo plano ya guardado)
      var rawAlert = (raw && raw.alertConfiguration) || {};
      result.alertConfiguration = {
        alertingEnabled: rawAlert.alertingEnabled != null ? rawAlert.alertingEnabled : DEFAULT_ALERT_CONFIGURATION.alertingEnabled,
        alertEnableEmail: rawAlert.alertEnableEmail != null ? rawAlert.alertEnableEmail : DEFAULT_ALERT_CONFIGURATION.alertEnableEmail,
        alertEnableSMS: rawAlert.alertEnableSMS != null ? rawAlert.alertEnableSMS : DEFAULT_ALERT_CONFIGURATION.alertEnableSMS,
        alertUsers: Array.isArray(rawAlert.alertUsers) ? angular.copy(rawAlert.alertUsers) : []
      };

      return result;
    }

    if (!$scope.agent.monitoringConfig) {
      $scope.agent.monitoringConfig = angular.copy(DEFAULT_MONITORING);
      $scope.originalAgent.monitoringConfig = angular.copy(DEFAULT_MONITORING);
    } else {
      $scope.agent.monitoringConfig = _mapMonitoringToUI($scope.agent.monitoringConfig);
      $scope.originalAgent.monitoringConfig = angular.copy($scope.agent.monitoringConfig);
    }
    console.log('✅ Agent Monitoring Config initialized:', $scope.agent.monitoringConfig);

    // 🔹 rzSlider rounds model values to the nearest step on first digest.
    // Re-snapshot originalAgent.monitoringConfig after that cycle so
    // hasChanges() doesn't fire false positives on tab entry.
    $timeout(function () {
      $scope.originalAgent.monitoringConfig = angular.copy($scope.agent.monitoringConfig);
    }, 0);

    // 🔹 rzSlider initializes lazily when its sub-tab becomes visible/selected.
    // Re-snapshot each time the user selects a monitoring sub-tab so that
    // any step-rounding applied on first render doesn't look like a user change.
    var _monitoringSnapshotDone = { cpu: false, memory: false, latency: false, pressure: false };
    $scope.onMonitoringSubtabSelect = function (tab) {
      if (!tab || _monitoringSnapshotDone[tab]) return;
      $timeout(function () {
        $scope.originalAgent.monitoringConfig[tab] = angular.copy($scope.agent.monitoringConfig[tab]);
        _monitoringSnapshotDone[tab] = true;
      }, 50);
    };

    // ─────────────────────────────────────────────
    // 🔹 [MONITORING] Opciones rzSlider para range sliders de threshold
    // ─────────────────────────────────────────────
    $scope.sliderOptions = {
      cpu: {
        threshold: {
          floor: 0, ceil: 100, step: 1,
          draggableRange: true, pushRange: true,
          minRange: $scope.limits.thresholdMinRangeCpu,
          translate: function (value, sliderId, label) {
            if (label === 'model') return '<span class="rz-label-clear">CLEAR ' + value + '%</span>';
            if (label === 'high') return '<span class="rz-label-set">SET ' + value + '%</span>';
            return value + '%';
          }
        },
        aggregationInterval: {
          floor: 1, ceil: $scope.limits.maxAggregationInterval, step: $scope.limits.stepAggregationInterval
        }
      },
      memory: {
        threshold: {
          floor: 0, ceil: 100, step: 1,
          draggableRange: true, pushRange: true,
          minRange: $scope.limits.thresholdMinRangeMemory,
          translate: function (value, sliderId, label) {
            if (label === 'model') return '<span class="rz-label-clear">CLEAR ' + value + '%</span>';
            if (label === 'high') return '<span class="rz-label-set">SET ' + value + '%</span>';
            return value + '%';
          }
        },
        aggregationInterval: {
          floor: 1, ceil: $scope.limits.maxAggregationInterval, step: $scope.limits.stepAggregationInterval
        }
      },
      latency: {
        threshold: {
          floor: 0, ceil: $scope.limits.maxLatency, step: $scope.limits.stepLatency,
          draggableRange: true, pushRange: true,
          minRange: $scope.limits.thresholdMinRangeLatency,
          translate: function (value, sliderId, label) {
            if (label === 'model') return '<span class="rz-label-clear">CLEAR ' + value + 'ms</span>';
            if (label === 'high') return '<span class="rz-label-set">SET ' + value + 'ms</span>';
            return value + 'ms';
          }
        },
        aggregationInterval: {
          floor: 1, ceil: $scope.limits.maxAggregationInterval, step: $scope.limits.stepAggregationInterval
        }
      },
      pressure: {
        threshold: {
          floor: 0, ceil: 100, step: 1,
          draggableRange: true, pushRange: true,
          minRange: $scope.limits.thresholdMinRangePressure,
          translate: function (value, sliderId, label) {
            if (label === 'model') return '<span class="rz-label-clear">CLEAR ' + value + '%</span>';
            if (label === 'high') return '<span class="rz-label-set">SET ' + value + '%</span>';
            return value + '%';
          }
        },
        aggregationInterval: {
          floor: 1, ceil: $scope.limits.maxAggregationInterval, step: $scope.limits.stepAggregationInterval
        }
      }
    };

    // ─────────────────────────────────────────────
    // 🔹 Validaciones cruzadas General tab
    // ─────────────────────────────────────────────
    // 🔹 [SAF FORWARDER] Max nunca puede quedar por debajo de Initial —
    // clampea en cualquier dirección para que la invariante se sostenga sin
    // importar cuál de los dos campos edita el usuario. Mismo patrón que
    // clampDynamicMaxReplicas/clampDynamicInitialReplicas más abajo.
    $scope.clampSafForwarderMaxCount = function () {
      if ($scope.agent.agentSafForwarderMaxCount < $scope.agent.agentSafForwarderInitialCount) {
        $scope.agent.agentSafForwarderMaxCount = $scope.agent.agentSafForwarderInitialCount;
      }
    };

    $scope.clampSafForwarderInitialCount = function () {
      if ($scope.agent.agentSafForwarderInitialCount > $scope.agent.agentSafForwarderMaxCount) {
        $scope.agent.agentSafForwarderMaxCount = $scope.agent.agentSafForwarderInitialCount;
      }
    };

    $scope.safUploadBatchCeiling = function () {
      const frames = Number($scope.agent.agentMetricsDiskMaxFrames);
      const count = Number($scope.agent.agentSafForwarderMaxCount);
      if (!Number.isFinite(frames) || frames <= 0 || !Number.isFinite(count) || count <= 0) return 1;
      return Math.min($scope.limits.maxSafUploadBatchSizeFrames, Math.ceil(frames / count));
    };
    $scope.safUploadBatchMinimum = function () {
      return Math.min($scope.limits.minSafUploadBatchSizeFrames, $scope.safUploadBatchCeiling());
    };
    $scope.clampSafUploadMaxBatchSizeFrames = function () {
      const cap = $scope.safUploadBatchCeiling();
      const floor = $scope.safUploadBatchMinimum();
      const clamp = function (value) {
        const n = Number(value);
        return Math.max(floor, Math.min(cap, Number.isFinite(n) ? Math.floor(n) : floor));
      };
      $scope.agent.agentSafUploadMaxBatchSizeFrames = clamp($scope.agent.agentSafUploadMaxBatchSizeFrames);
      $scope.agent.agentSafUploadInitialBatchSizeFrames = Math.min(
        clamp($scope.agent.agentSafUploadInitialBatchSizeFrames), $scope.agent.agentSafUploadMaxBatchSizeFrames);
    };
    $scope.clampSafUploadInitialBatchSizeFrames = $scope.clampSafUploadMaxBatchSizeFrames;
    $scope.$watchGroup([
      'agent.agentMetricsDiskMaxFrames', 'agent.agentSafForwarderMaxCount',
      'agent.agentSafUploadInitialBatchSizeFrames', 'agent.agentSafUploadMaxBatchSizeFrames'
    ], $scope.clampSafUploadMaxBatchSizeFrames);

    // 🔹 [SAF FORWARDER AUTO-SCALING] Scale-Up can never be set below
    // Scale-Down — same asymmetric pattern as the interceptor's own
    // backpressureScaleDownThresholdMax/onBackpressureScaleUpThresholdChange
    // (EditBizInterceptorDialogController.js): Scale-Down's own slider max is
    // dynamically capped at the CURRENT Scale-Up value (so it can never be
    // dragged past it), while Scale-Up's ceiling is the static limit and it
    // instead pushes Scale-Down down if raising/lowering Scale-Up would
    // otherwise leave Scale-Down above it.
    $scope.safForwarderScaleDownThresholdMax = function () {
      return Math.min(
        $scope.limits.maxSafForwarderScaleThresholdPct,
        $scope.agent.agentSafForwarderScaleUpPressureThresholdPct
      );
    };

    $scope.clampSafForwarderScaleDownThresholdPct = function () {
      var max = $scope.safForwarderScaleDownThresholdMax();
      if ($scope.agent.agentSafForwarderScaleDownPressureThresholdPct > max) {
        $scope.agent.agentSafForwarderScaleDownPressureThresholdPct = max;
      }
    };

    $scope.onSafForwarderScaleUpThresholdChange = function () {
      var max = $scope.safForwarderScaleDownThresholdMax();
      if ($scope.agent.agentSafForwarderScaleDownPressureThresholdPct > max) {
        $scope.agent.agentSafForwarderScaleDownPressureThresholdPct = max;
      }
    };

    $scope.validateLogBatchSize = function () {
      if ($scope.agent.agentLogBatchSize > $scope.agent.agentLogBufferSize) {
        $scope.agent.agentLogBatchSize = $scope.agent.agentLogBufferSize;
      }
    };

    // 🔹 [AUTOSCALING] Max nunca puede quedar por debajo de Initial — clampea
    // en cualquier dirección para que la invariante se sostenga sin importar
    // cuál de los dos campos edita el usuario.
    $scope.clampDynamicMaxReplicas = function () {
      if ($scope.agent.osConfig.dynamicMaxReplicas < $scope.agent.osConfig.dynamicInitialReplicas) {
        $scope.agent.osConfig.dynamicMaxReplicas = $scope.agent.osConfig.dynamicInitialReplicas;
      }
    };

    $scope.clampDynamicInitialReplicas = function () {
      if ($scope.agent.osConfig.dynamicInitialReplicas > $scope.agent.osConfig.dynamicMaxReplicas) {
        $scope.agent.osConfig.dynamicMaxReplicas = $scope.agent.osConfig.dynamicInitialReplicas;
      }
    };

    // Misma invariante que arriba, para el rango de CPU asignada por pod.
    $scope.clampMaxCpuAssignment = function () {
      if ($scope.agent.osConfig.maxCpuAssignment < $scope.agent.osConfig.minCpuAssignment) {
        $scope.agent.osConfig.maxCpuAssignment = $scope.agent.osConfig.minCpuAssignment;
      }
    };

    $scope.clampMinCpuAssignment = function () {
      if ($scope.agent.osConfig.minCpuAssignment > $scope.agent.osConfig.maxCpuAssignment) {
        $scope.agent.osConfig.maxCpuAssignment = $scope.agent.osConfig.minCpuAssignment;
      }
    };

    // ─────────────────────────────────────────────
    // 🔹 Clamp para mantener valores en rango
    //    Soporta paths profundos: 'agent.monitoringConfig.cpu.thresholdSet'
    // ─────────────────────────────────────────────
    $scope.clampValue = function (modelPath, min, max) {
      const parts = modelPath.split('.');
      let target = $scope;
      for (let i = 0; i < parts.length - 1; i++) target = target[parts[i]];
      const prop = parts[parts.length - 1];
      let value = target[prop];
      if (typeof value === 'string') value = parseFloat(value);
      if (isNaN(value) || value === null || value === undefined) { target[prop] = min; return; }
      if (value < min) target[prop] = min;
      else if (value > max) target[prop] = max;
    };

    // Network Settings — plain [min, max] clamps, no sibling-field ceiling
    // (unlike the SAF Forwarder Initial/Max pairs above, Connect/Read
    // Timeout are two independent values, each with its own fixed range).
    $scope.clampNetworkConnectTimeoutSeconds = function () {
      $scope.clampValue('agent.networkSettings.connectTimeoutSeconds',
        $scope.limits.minNetworkConnectTimeoutSeconds, $scope.limits.maxNetworkConnectTimeoutSeconds);
    };

    $scope.clampNetworkReadTimeoutSeconds = function () {
      $scope.clampValue('agent.networkSettings.readTimeoutSeconds',
        $scope.limits.minNetworkReadTimeoutSeconds, $scope.limits.maxNetworkReadTimeoutSeconds);
    };

    // ─────────────────────────────────────────────
    // 🔹 [NETWORK SETTINGS] Proxy Host/Port validation — gates Save the same
    // way isValidInternalURL/isValidURL already gate it for Security
    // External/Internal. Reuses gatewayPattern (same hostname-or-IP regex
    // already used for those two fields) rather than inventing a second
    // one — a proxy host is the same "hostname or IP, optional :port/path"
    // shape. Only enforced while proxyEnabled is true; a disabled proxy's
    // stale/blank host+port never blocks Save.
    // ─────────────────────────────────────────────
    $scope.isValidProxyHost = true;
    $scope.isValidProxyPort = true;
    $scope.isValidProxyUsername = true;
    $scope.proxyHostErrorMessage = '';
    $scope.proxyPortErrorMessage = '';
    $scope.proxyUsernameErrorMessage = '';

    $scope.validateProxyConfig = function () {
      var ns = $scope.agent.networkSettings || {};

      if (!ns.proxyEnabled) {
        $scope.isValidProxyHost = true;
        $scope.isValidProxyPort = true;
        $scope.isValidProxyUsername = true;
        $scope.proxyHostErrorMessage = '';
        $scope.proxyPortErrorMessage = '';
        $scope.proxyUsernameErrorMessage = '';
        return;
      }

      var host = (ns.proxyHost || '').trim();
      $scope.isValidProxyHost = !!(host && host.match($scope.gatewayPattern));
      $scope.proxyHostErrorMessage = $scope.isValidProxyHost
        ? ''
        : 'Enter a valid hostname or IP (e.g. proxy.internal.company.com)';

      var port = ns.proxyPort;
      $scope.isValidProxyPort = Number.isInteger(port) && port >= 1 && port <= 65535;
      $scope.proxyPortErrorMessage = $scope.isValidProxyPort
        ? ''
        : 'Enter a valid port (1-65535)';

      // Unlike Username being merely "Optional" on every OTHER form on this
      // dialog, an enabled proxy always needs SOME identity to authenticate
      // as — an unauthenticated proxy is configured with the switch off
      // wherever that's meant, not with a blank username here.
      var username = (ns.proxyUsername || '').trim();
      $scope.isValidProxyUsername = !!username;
      $scope.proxyUsernameErrorMessage = $scope.isValidProxyUsername
        ? ''
        : 'Username is required when the proxy is enabled';
    };

    // ─────────────────────────────────────────────
    // 🔹 [NETWORK SETTINGS] Proxy password confirmation — proxyConfirm.password
    // is a UI-only field (never sent to the backend, never present on
    // agent.networkSettings itself) that exists purely so a typo in a new
    // proxy password is caught here instead of silently locking interceptors
    // out of the proxy after Save. Only enforced when the user is actually
    // setting a new password — a blank password field already means "keep
    // the current one" (see TelemetryAgentService's own preserve-if-blank
    // handling), so there's nothing to confirm in that case.
    // Deliberately nested under an object (not a bare $scope.proxyPasswordConfirm)
    // — this field's own input sits inside ng-if="agent.networkSettings.proxyEnabled",
    // which creates a child scope. A bare top-level ng-model there would
    // have the child scope shadow it with its own copy on first write
    // (the classic Angular "dot rule"), leaving this controller's own
    // $scope.proxyPasswordConfirm permanently stuck at '' while the input
    // itself displayed the typed value fine — exactly that bug was caught
    // live (typed matching passwords, DOM/child-scope both correct, yet
    // validation kept reporting a mismatch) before switching to this
    // dotted form, which always resolves through the SAME object.
    // ─────────────────────────────────────────────
    $scope.proxyConfirm = { password: '' };
    $scope.isValidProxyPasswordConfirm = true;
    $scope.proxyPasswordConfirmErrorMessage = '';

    $scope.validateProxyPasswordConfirm = function () {
      var ns = $scope.agent.networkSettings || {};
      var password = ns.proxyPassword || '';

      if (!ns.proxyEnabled || !password) {
        $scope.isValidProxyPasswordConfirm = true;
        $scope.proxyPasswordConfirmErrorMessage = '';
        return;
      }

      $scope.isValidProxyPasswordConfirm = ($scope.proxyConfirm.password === password);
      $scope.proxyPasswordConfirmErrorMessage = $scope.isValidProxyPasswordConfirm
        ? ''
        : 'Passwords do not match';
    };

    // ─────────────────────────────────────────────
    // 🔹 Lookup dinámico del externalGw
    // ─────────────────────────────────────────────
    $scope.lookupAgentAddress = function () {
      if (!$scope.agent || !$scope.agent.agentId) return;
      const isPceMode = ConfigurationService.getFromCache('bizmetry.platform.pce_mode');
      $scope.agent.securityConfig.external.host = isPceMode
        ? 'https://' + $scope.agent.agentIp
        : $scope.agent.externalIp;
    };

    // ─────────────────────────────────────────────
    // 🔹 Detección de cambios
    // ─────────────────────────────────────────────

    /**
     * Compara dos arrays de alertUsers por contenido (id + email).
     * Retorna true si son diferentes.
     */
    function _alertUsersChanged(current, original) {
      var curr = current || [];
      var orig = original || [];
      if (curr.length !== orig.length) return true;
      for (var i = 0; i < curr.length; i++) {
        if (curr[i].id !== orig[i].id || curr[i].email !== orig[i].email) return true;
      }
      return false;
    }

    $scope.hasChanges = function () {
      if (!$scope.originalAgent || !$scope.agent) return false;

      const normalize = val => (typeof val === 'string' ? val.trim() : val);

      // ── Campos simples (path-based) ───────────────
      const fields = [
        // General
        'agentMaxDiskBufferSize', 'agentMetricsDiskMaxFrames', 'agentMaxMemBufferSize', 'agentLogBufferSize',
        'agentSafForwarderInitialCount', 'agentSafForwarderMaxCount',
        'agentSafUploadInitialBatchSizeFrames', 'agentSafUploadMaxBatchSizeFrames',
        'agentSafForwarderScaleDownPressureThresholdPct', 'agentSafForwarderScaleUpPressureThresholdPct',
        'agentLogBatchSize', 'agentSyncInterval', 'agentMaxPayloadSizeMb', 'agentBulkFetchBatchSize',
        'agentLogLevel', 'agentName', 'isPubliclyExposed', 'isInternallyExposed',
        // Network Settings — read by every biz interceptor linked to this
        // agent's environment (see BizInterceptorServiceImpl#resolveAgentInfo),
        // never by this agent's own process. proxyPassword is safe to diff
        // here because BOTH copies get it blanked at load time (see the
        // seed block above) — only an explicit new password shows as changed.
        'networkSettings.connectTimeoutSeconds', 'networkSettings.readTimeoutSeconds',
        'networkSettings.proxyEnabled', 'networkSettings.proxyHost', 'networkSettings.proxyPort',
        'networkSettings.proxyScheme', 'networkSettings.proxyUsername', 'networkSettings.proxyPassword',
        // Security
        'securityConfig.external.host',
        'securityConfig.external.privateKey',
        'securityConfig.external.privateKeyPassphrase',
        'securityConfig.internal.privateKey',
        'securityConfig.internal.host',
        'securityConfig.internal.networkTopology',
        // Deployment
        'osConfig.agentOsTypeId',
        'osConfig.registryURL',
        'osConfig.registryProject',
        'osConfig.registryUserName',
        'osConfig.registryPassword',
        'osConfig.enabled',
        'osConfig.agentIngressTypeId',
        // SLAs — CPU
        'monitoringConfig.cpu.enabled',
        'monitoringConfig.cpu.alertingEnabled',
        'monitoringConfig.cpu.thresholdClear',
        'monitoringConfig.cpu.thresholdSet',
        'monitoringConfig.cpu.setTime',
        'monitoringConfig.cpu.resetTime',
        // SLAs — Memory
        'monitoringConfig.memory.enabled',
        'monitoringConfig.memory.alertingEnabled',
        'monitoringConfig.memory.thresholdClear',
        'monitoringConfig.memory.thresholdSet',
        'monitoringConfig.memory.setTime',
        'monitoringConfig.memory.resetTime',
        // SLAs — Latency
        'monitoringConfig.latency.enabled',
        'monitoringConfig.latency.alertingEnabled',
        'monitoringConfig.latency.thresholdClear',
        'monitoringConfig.latency.thresholdSet',
        'monitoringConfig.latency.setTime',
        'monitoringConfig.latency.resetTime',
        // SLAs — Pressure
        'monitoringConfig.pressure.enabled',
        'monitoringConfig.pressure.alertingEnabled',
        'monitoringConfig.pressure.thresholdClear',
        'monitoringConfig.pressure.thresholdSet',
        'monitoringConfig.pressure.setTime',
        'monitoringConfig.pressure.resetTime',
        // Alerting tab — scalar fields
        'monitoringConfig.alertConfiguration.alertingEnabled',
        'monitoringConfig.alertConfiguration.alertEnableEmail',
        'monitoringConfig.alertConfiguration.alertEnableSMS'
      ];

      // ── Lógica especial para Deployment ──────────
      const currentOS = $scope.agent.osConfig || {};
      const originalOS = $scope.originalAgent.osConfig || {};
      const osEnabledNow = !!currentOS.enabled;
      const osEnabledBefore = !!originalOS.enabled;

      if (osEnabledNow !== osEnabledBefore) return true;

      if (osEnabledNow === true) {
        if (normalize(currentOS.agentOsTypeId) !== normalize(originalOS.agentOsTypeId)) return true;
      } else {
        const deploymentChanged =
          normalize(currentOS.agentOsTypeId) !== normalize(originalOS.agentOsTypeId) ||
          normalize(currentOS.agentIngressTypeId) !== normalize(originalOS.agentIngressTypeId) ||
          normalize(currentOS.registryURL) !== normalize(originalOS.registryURL) ||
          normalize(currentOS.registryProject) !== normalize(originalOS.registryProject) ||
          normalize(currentOS.registryUserName) !== normalize(originalOS.registryUserName) ||
          normalize(currentOS.registryPassword) !== normalize(originalOS.registryPassword);
        if (deploymentChanged) return true;
      }

      // AutoScaling — replica count/mode always require a StatefulSet
      // rollout, regardless of the "provide params at deploy-time" toggle.
      const capacityChanged =
        normalize(currentOS.capacityMode) !== normalize(originalOS.capacityMode) ||
        normalize(currentOS.fixedReplicas) !== normalize(originalOS.fixedReplicas) ||
        normalize(currentOS.dynamicInitialReplicas) !== normalize(originalOS.dynamicInitialReplicas) ||
        normalize(currentOS.dynamicMaxReplicas) !== normalize(originalOS.dynamicMaxReplicas) ||
        normalize(currentOS.cpuThresholdPercent) !== normalize(originalOS.cpuThresholdPercent) ||
        normalize(currentOS.memoryThresholdPercent) !== normalize(originalOS.memoryThresholdPercent) ||
        normalize(currentOS.thresholdDurationMinutes) !== normalize(originalOS.thresholdDurationMinutes) ||
        normalize(currentOS.minCpuAssignment) !== normalize(originalOS.minCpuAssignment) ||
        normalize(currentOS.maxCpuAssignment) !== normalize(originalOS.maxCpuAssignment);
      if (capacityChanged) return true;

      // ── Alerting — alertUsers (array deep check) ─
      const currentAlert = ($scope.agent.monitoringConfig || {}).alertConfiguration || {};
      const originalAlert = ($scope.originalAgent.monitoringConfig || {}).alertConfiguration || {};
      if (_alertUsersChanged(currentAlert.alertUsers, originalAlert.alertUsers)) return true;

      // ── Resto de campos simples ───────────────────
      return fields.some(function (field) {
        // osConfig fields are handled above (except osConfig.enabled which is in fields for the toggle)
        if (field.startsWith('osConfig.') && field !== 'osConfig.enabled') return false;

        const keys = field.split('.');
        let currentValue = $scope.agent;
        let originalValue = $scope.originalAgent;

        for (let i = 0; i < keys.length; i++) {
          currentValue = currentValue && currentValue.hasOwnProperty(keys[i]) ? currentValue[keys[i]] : undefined;
          originalValue = originalValue && originalValue.hasOwnProperty(keys[i]) ? originalValue[keys[i]] : undefined;
        }

        return normalize(currentValue) !== normalize(originalValue);
      });
    };

    // ─────────────────────────────────────────────
    // 🔹 Cancelar edición
    // ─────────────────────────────────────────────
    $scope.cancel = function () {
      if ($scope.hasChanges()) {
        dialogService.showDecisionDialog(
          'Discard Changes?',
          'You have unsaved changes. Are you sure you want to discard them?'
        ).then(function (decision) {
          if (decision === 'proceed') $mdDialog.cancel();
        });
      } else {
        $mdDialog.cancel();
      }
    };

    // ─────────────────────────────────────────────
    // 🔹 Guardar configuración del agente
    //    El payload incluye monitoringConfig completo:
    //      - cpu/memory/latency con enabled + alertingEnabled + thresholds + times
    //      - alertConfiguration con alertingEnabled + channels + alertUsers
    // ─────────────────────────────────────────────
    $scope.clampMaxPayloadSizeMb = function () {
      if ($scope.agent.agentMaxPayloadSizeMb == null || !Number.isFinite(Number($scope.agent.agentMaxPayloadSizeMb))) {
        $scope.agent.agentMaxPayloadSizeMb = $scope.limits.defaultMaxPayloadSizeMb;
      }
      $scope.agent.agentMaxPayloadSizeMb = Math.round(Number($scope.agent.agentMaxPayloadSizeMb));
      $scope.clampValue('agent.agentMaxPayloadSizeMb', $scope.limits.minMaxPayloadSizeMb, $scope.limits.maxMaxPayloadSizeMb);
    };

    $scope.clampBulkFetchBatchSize = function () {
      if ($scope.agent.agentBulkFetchBatchSize == null || !Number.isFinite(Number($scope.agent.agentBulkFetchBatchSize))) {
        $scope.agent.agentBulkFetchBatchSize = $scope.limits.defaultBulkFetchBatchSize;
      }
      $scope.agent.agentBulkFetchBatchSize = Math.round(Number($scope.agent.agentBulkFetchBatchSize));
      $scope.clampValue('agent.agentBulkFetchBatchSize', $scope.limits.minBulkFetchBatchSize, $scope.limits.maxBulkFetchBatchSize);
    };

    $scope.save = function () {
      $scope.clampMaxPayloadSizeMb();
      $scope.clampBulkFetchBatchSize();
      $timeout(function () {

        angular.copy($scope.agent, agent);

        // Enriquecer con nombre legible de OS Type
        if (agent.osConfig && agent.osConfig.agentOsTypeId) {
          const osType = ($scope.agentOsTypes || []).find(ost => ost.refId === agent.osConfig.agentOsTypeId);
          if (osType) agent.osConfig.agentOsTypeName = osType.refObjectLabel;
        }

        // Enriquecer con nombre legible de Ingress Type
        if (agent.osConfig && agent.osConfig.agentIngressTypeId) {
          const ingressType = ($scope.agentIngressTypes || []).find(it => it.refId === agent.osConfig.agentIngressTypeId);
          if (ingressType) agent.osConfig.agentIngressTypeName = ingressType.refObjectLabel;
        }

        // Garantizar que alertConfiguration esté siempre presente y bien formado
        // (por si el agente fue guardado antes de que existiera esta estructura)
        var mc = agent.monitoringConfig;
        var ac = mc.alertConfiguration || {};
        mc.alertConfiguration = {
          alertingEnabled: !!ac.alertingEnabled,
          alertEnableEmail: !!ac.alertEnableEmail,
          alertEnableSMS: !!ac.alertEnableSMS,
          alertUsers: Array.isArray(ac.alertUsers) ? ac.alertUsers.map(function (u) {
            return {
              id: u.id,
              email: u.email,
              mobile2FANumber: u.mobile2FANumber || null,
              userEnabled: _recipientEnabled(u)
            };
          }) : []
        };

        // Garantizar que cada métrica lleve su alertingEnabled
        ['cpu', 'memory', 'latency', 'pressure'].forEach(function (m) {
          if (mc[m]) mc[m].alertingEnabled = !!mc[m].alertingEnabled;
        });

        console.log('ABOUT TO SAVE AGENT=', agent);

        TelemetryAgentService.saveAgentConfig(agent)
          .then(function () {
            $rootScope.$broadcast('agentList:refresh');
            $mdDialog.hide();
          })
          .catch(function (error) {
            $rootScope.$broadcast('agentList:refresh');

            if (error.status === 403) {
              dialogService.showErrorDialog(
                'Agent access is forbidden',
                'The agent you are trying to reach out either does not match with the agent ID being configured or does belong to a different account'
              );
            } else if (error.status === 503) {
              dialogService.showErrorDialog(
                'Agent is Unreachable',
                'The external gateway address for the agent could not be reached. Please verify the address is correct and that it is configured with a proper certificate.'
              );
            } else if (error.status === 500) {
              const backendMessage = error.data && error.data.message
                ? error.data.message
                : 'An unexpected error occurred on the server.';
              console.error('❌ Internal server error saving agent config:', backendMessage);
              dialogService.showErrorDialog(
                'Unexpected Server Error',
                'The server encountered an internal error while saving the agent configuration. Please try again or contact support if the issue persists.<br><br>' +
                '<code style="font-size: 11px; color: #d32f2f;">' + backendMessage + '</code>'
              );
            } else if (error.status === 422) {
              console.warn('Agent is not in ONLINE state', error);
              $mdDialog.hide();
            }
          });
      }, 0);
    };

    // ─────────────────────────────────────────────
    // 🔹 Security Internal
    // ─────────────────────────────────────────────
    $scope.isValidInternalURL = !!(
      $scope.agent &&
      $scope.agent.securityConfig &&
      $scope.agent.securityConfig.internal &&
      $scope.agent.securityConfig.internal.host &&
      $scope.agent.securityConfig.internal.host.match($scope.gatewayPattern)
    );

    $scope.internalHostChanged = false;

    $scope.originalInternalHost = $scope.agent && $scope.agent.securityConfig && $scope.agent.securityConfig.internal
      ? $scope.agent.securityConfig.internal.host
      : null;

    $scope.keyGenerated = $scope.isValidInternalURL &&
      $scope.agent &&
      $scope.agent.securityConfig &&
      $scope.agent.securityConfig.internal &&
      $scope.agent.securityConfig.internal.privateKey &&
      $scope.agent.securityConfig.internal.privateKey !== '';

    $scope.onInternalHostChange = function () {
      $scope.isValidInternalURL = !!(
        $scope.agent.securityConfig.internal.host &&
        $scope.agent.securityConfig.internal.host.match($scope.gatewayPattern)
      );
      $scope.internalHostChanged = ($scope.agent.securityConfig.internal.host !== $scope.originalInternalHost);
      $scope.agent.securityConfig.internal.privateKey = '';
      $scope.keyGenerated = false;
    };

    // ─────────────────────────────────────────────
    // 🔹 Validez del formulario
    // ─────────────────────────────────────────────
    $scope.updateFormValidity = function () {
      const osConfig = $scope.agent.osConfig || {};

      const exposureValid =
        (
          $scope.agent.isPubliclyExposed === false ||
          ($scope.agent.isPubliclyExposed === true && $scope.isValidURL === true && $scope.isPrivateKeyValid === true)
        ) &&
        (
          $scope.agent.isInternallyExposed === false ||
          $scope.agent.securityConfig?.internal?.networkTopology === 'CO_LOCATED' ||
          (
            $scope.agent.isInternallyExposed === true &&
            $scope.isValidInternalURL === true &&
            $scope.keyGenerated === true &&
            (!!osConfig.enabled || !!osConfig.agentIngressTypeId)
          )
        );

      const deploymentValid = !!osConfig.enabled || (
        !!osConfig.agentOsTypeId &&
        !!(osConfig.registryURL && osConfig.registryURL.trim()) &&
        !!(osConfig.registryProject && osConfig.registryProject.trim()) &&
        !!(osConfig.registryUserName && osConfig.registryUserName.trim()) &&
        !!(osConfig.registryPassword && osConfig.registryPassword.trim())
      );

      // 🔹 [ALERTING] Si alerting está ON: requiere al menos un canal y un destinatario
      const ac = ($scope.agent.monitoringConfig || {}).alertConfiguration || {};
      const alertingValid = !ac.alertingEnabled || (
        (ac.alertEnableEmail || ac.alertEnableSMS) &&
        Array.isArray(ac.alertUsers) && ac.alertUsers.length > 0
      );

      // 🔹 [AUTOSCALING] Fixed: réplicas dentro de [minReplicas, maxReplicas]
      // (bizmetry-shared-services). Dynamic: initial/max ambos en rango y
      // initial <= max.
      const inReplicaRange = function (n) {
        return Number.isInteger(n) && n >= $scope.limits.minReplicas && n <= $scope.limits.maxReplicas;
      };
      const capacityValid = osConfig.capacityMode === 'DYNAMIC'
        ? inReplicaRange(osConfig.dynamicInitialReplicas) &&
          inReplicaRange(osConfig.dynamicMaxReplicas) &&
          osConfig.dynamicInitialReplicas <= osConfig.dynamicMaxReplicas &&
          Number.isInteger(osConfig.thresholdDurationMinutes) &&
          osConfig.thresholdDurationMinutes >= $scope.limits.minThresholdDuration &&
          osConfig.thresholdDurationMinutes <= $scope.limits.maxThresholdDuration
        : inReplicaRange(osConfig.fixedReplicas);

      // 🔹 [AUTOSCALING] CPU asignada por pod, min/max dentro de rango y
      // min <= max — mismo criterio que arriba, con el piso/techo propios.
      const inCpuAssignmentRange = function (n) {
        return Number.isInteger(n) && n >= $scope.limits.minCpuAssignment && n <= $scope.limits.maxCpuAssignment;
      };
      const cpuAssignmentValid = inCpuAssignmentRange(osConfig.minCpuAssignment) &&
        inCpuAssignmentRange(osConfig.maxCpuAssignment) &&
        osConfig.minCpuAssignment <= osConfig.maxCpuAssignment;

      // 🔹 [NETWORK SETTINGS] Proxy Host/Port/password-confirm only need to
      // be valid while the proxy switch is on — see validateProxyConfig/
      // validateProxyPasswordConfirm.
      const networkValid = $scope.isValidProxyHost && $scope.isValidProxyPort && $scope.isValidProxyUsername
        && $scope.isValidProxyPasswordConfirm;

      $scope.isFormValid = exposureValid && deploymentValid && alertingValid && capacityValid
        && cpuAssignmentValid && networkValid;

      const reasons = [];
      if (!exposureValid) {
        if ($scope.agent.isPubliclyExposed && ($scope.isValidURL !== true || $scope.isPrivateKeyValid !== true)) {
          reasons.push('Security External: validate the gateway address and its matching private key.');
        }
        if ($scope.agent.isInternallyExposed && $scope.agent.securityConfig?.internal?.networkTopology !== 'CO_LOCATED') {
          if ($scope.isValidInternalURL !== true) reasons.push('Security Internal: enter a valid internal address.');
          if ($scope.keyGenerated !== true) reasons.push('Security Internal: generate the key pair for the internal address.');
          if (!osConfig.enabled && !osConfig.agentIngressTypeId) reasons.push('Security Internal: select an ingress type.');
        }
      }
      if (!deploymentValid) reasons.push('Deployment: complete the architecture and registry URL, project, username and password, or provide them at deploy-time.');
      if (!alertingValid) {
        if (!ac.alertEnableEmail && !ac.alertEnableSMS) reasons.push('Alerting: select at least one notification channel.');
        if (!Array.isArray(ac.alertUsers) || !ac.alertUsers.length) reasons.push('Alerting: select at least one recipient or disable alerting.');
      }
      if (!capacityValid) reasons.push('Capacity: check replica limits and, for dynamic capacity, the threshold duration. Initial replicas cannot exceed the maximum.');
      if (!cpuAssignmentValid) reasons.push('Capacity / CPU / Memory Resources: check CPU limits. Minimum CPU cannot exceed maximum CPU.');
      if (!networkValid) {
        if (!$scope.isValidProxyHost) reasons.push('Tuning / Network: enter a valid proxy host.');
        if (!$scope.isValidProxyPort) reasons.push('Tuning / Network: enter a proxy port between 1 and 65535.');
        if (!$scope.isValidProxyUsername) reasons.push('Tuning / Network: enter the proxy username.');
        if (!$scope.isValidProxyPasswordConfirm) reasons.push('Tuning / Network: proxy passwords must match.');
      }
      $scope.saveValidationReasons = reasons;
    };

    $scope.saveDisabledReason = function () {
      if (!$scope.isFormValid) {
        return ($scope.saveValidationReasons || []).join('\n') || 'Complete the required configuration before saving.';
      }
      return !$scope.hasChanges() ? 'No changes to save.' : '';
    };

    $scope.$watch('agent.isPubliclyExposed', $scope.updateFormValidity);
    $scope.$watch('agent.isInternallyExposed', $scope.updateFormValidity);
    $scope.$watch('agent.securityConfig.internal.networkTopology', $scope.updateFormValidity);
    $scope.$watch('isValidURL', $scope.updateFormValidity);
    $scope.$watch('isPrivateKeyValid', $scope.updateFormValidity);
    $scope.$watch('isValidInternalURL', $scope.updateFormValidity);
    $scope.$watch('keyGenerated', $scope.updateFormValidity);
    $scope.$watch('agent.osConfig.enabled', $scope.updateFormValidity);
    $scope.$watchCollection('agent.osConfig', $scope.updateFormValidity);

    $scope.$watch('agent.networkSettings.proxyEnabled', function () {
      $scope.validateProxyConfig();
      $scope.updateFormValidity();
    });
    $scope.$watch('agent.networkSettings.proxyHost', function () {
      $scope.validateProxyConfig();
      $scope.updateFormValidity();
    });
    $scope.$watch('agent.networkSettings.proxyPort', function () {
      $scope.validateProxyConfig();
      $scope.updateFormValidity();
    });
    $scope.$watch('agent.networkSettings.proxyUsername', function () {
      $scope.validateProxyConfig();
      $scope.updateFormValidity();
    });
    $scope.$watch('agent.networkSettings.proxyPassword', function () {
      $scope.validateProxyPasswordConfirm();
      $scope.updateFormValidity();
    });
    $scope.$watch('proxyConfirm.password', function () {
      $scope.validateProxyPasswordConfirm();
      $scope.updateFormValidity();
    });
    $scope.validateProxyConfig();
    $scope.validateProxyPasswordConfirm();

    $scope.$watch('agent.monitoringConfig.alertConfiguration.alertingEnabled', $scope.updateFormValidity);
    $scope.$watch('agent.monitoringConfig.alertConfiguration.alertEnableEmail', $scope.updateFormValidity);
    $scope.$watch('agent.monitoringConfig.alertConfiguration.alertEnableSMS', $scope.updateFormValidity);
    $scope.$watchCollection('agent.monitoringConfig.alertConfiguration.alertUsers', $scope.updateFormValidity);

    // ─────────────────────────────────────────────
    // 🔹 Toggle públicamente expuesto
    // ─────────────────────────────────────────────
    $scope.togglePublicAgent = function () {
      if ($scope.agent.isPubliclyExposed) {
        $scope.validateExternalAgentGwUrl();
      } else {
        $scope.urlErrorMessage = '';
      }
    };

    // ─────────────────────────────────────────────
    // 🔹 Validación de la URL externa
    // ─────────────────────────────────────────────
    $scope.validateExternalAgentGwUrl = function () {
      var deferred = $q.defer();

      if (!$scope.agent.securityConfig) {
        $scope.isValidURL = false;
        $scope.urlErrorMessage = 'Security configuration is missing.';
        deferred.reject('Security configuration missing');
        return deferred.promise;
      }

      if (!$scope.agent.securityConfig.external) $scope.agent.securityConfig.external = {};

      $scope.isValidatingGwUrl = true;

      if (
        $scope.agent.securityConfig.external.host &&
        $scope.agent.securityConfig.external.host.match($scope.gatewayPattern)
      ) {
        TelemetryAgentService.validateAgentURL('https://' + $scope.agent.securityConfig.external.host)
          .then(function (response) {
            $scope.isValidURL = true;
            $scope.urlErrorMessage = '';

            if (!$scope.agent.securityConfig.external.public) $scope.agent.securityConfig.external.public = {};
            if (!$scope.agent.securityConfig.external.public.certificates) $scope.agent.securityConfig.external.public.certificates = [];

            if (response.data && response.data.certificates && response.data.certificates.length > 0) {
              $scope.agent.securityConfig.external.public.certificates = response.data.certificates.map(cert => ({
                pem: cert.pem, cn: cert.cn, issuer: cert.issuer,
                validFrom: cert.validFrom, validTo: cert.validTo,
                signatureAlgorithm: cert.signatureAlgorithm,
                serialNumber: cert.serialNumber, subject: cert.subject
              }));
            }

            $scope.isValidatingGwUrl = false;
            deferred.resolve();
          })
          .catch(function (error) {
            $scope.isValidURL = false;
            $scope.urlErrorMessage = error.data
              ? `${error.data.error}: ${error.data.message}`
              : 'Invalid / Non-reachable URL, please verify';
            $scope.isValidatingGwUrl = false;
            deferred.reject(error);
          });
      } else {
        $scope.isValidURL = false;
        $scope.urlErrorMessage = 'Invalid address. Enter only the hostname or IP (e.g. gateway.example.com)';
        $scope.isValidatingGwUrl = false;
        deferred.reject('Invalid URL format');
      }

      return deferred.promise;
    };

    // ─────────────────────────────────────────────
    // 🔹 Validación de par de claves TLS
    // ─────────────────────────────────────────────
    $scope.validateKeys = function () {
      const privateKey = $scope.agent?.securityConfig?.external?.privateKey;
      const publicKey = $scope.agent?.securityConfig?.external?.public?.certificates?.[0]?.pem;
      const privateKeyPassphrase = $scope.agent?.securityConfig?.external?.privateKeyPassphrase;

      if (privateKey && publicKey) {
        TelemetryAgentService.validateKeys(privateKey, publicKey, privateKeyPassphrase)
          .then(function (response) {
            $scope.isPrivateKeyValid = response.keysMatch;
            console.log('✅ Private key match:', $scope.isPrivateKeyValid);
          })
          .catch(function (error) {
            $scope.isPrivateKeyValid = false;
            console.error('❌ Error validating keys:', error);
          });
      } else {
        $scope.isPrivateKeyValid = false;
        console.error('Private key or public key is missing');
      }
    };

    // Opening an existing configuration must not probe the running agent:
    // an outage must not prevent changing settings needed for recovery.
    // Reachability is checked only on explicit external-address/exposure edits.
    const savedExternalHost = $scope.agent?.securityConfig?.external?.host;
    $scope.isValidURL = !!(savedExternalHost && savedExternalHost.match($scope.gatewayPattern));
    $scope.isValidatingGwUrl = false;
    $scope.urlErrorMessage = $scope.isValidURL ? '' : 'Invalid address. Enter only the hostname or IP (e.g. gateway.example.com)';
    // Key-pair validation uses the stored certificate, not agent reachability.
    $scope.validateKeys();

    $scope.debounceValidateKeys = function () {
      if ($scope.debounceTimeout) $timeout.cancel($scope.debounceTimeout);
      $scope.debounceTimeout = $timeout(function () { $scope.validateKeys(); }, 500);
    };

    $scope.debounceValidateExternalUrl = function () {
      if ($scope.debounceTimeout) $timeout.cancel($scope.debounceTimeout);
      $scope.debounceTimeout = $timeout(function () { $scope.validateExternalAgentGwUrl(); }, 500);
    };

    $scope.$watch('agent.isInternallyExposed', function (newValue) {
      console.log('isInternallyExposed changed:', newValue);
      console.log('$scope.isValidInternalURL --->', $scope.isValidInternalURL);
    });

    // ─────────────────────────────────────────────
    // 🔹 Generación de certificado interno
    // ─────────────────────────────────────────────
    $scope.generateChainCert = function () {
      const hostname = 'https://' + $scope.agent.securityConfig.internal.host;
      const pceMode = PlatformInfoService.getPlatformInfo().pceMode;
      console.log('pcemode=', pceMode);
      console.log('hostname=', hostname);

      if (!hostname) { console.error('Hostname is required!'); return; }

      TelemetryAgentService.generateChainCert(hostname, pceMode)
        .then(function (response) {
          console.log('Certificate chain generated successfully:', response);
          if (response.data && response.data.certificates && response.data.certificates.length > 0) {
            $scope.agent.securityConfig.internal.public = $scope.agent.securityConfig.internal.public || {};
            $scope.agent.securityConfig.internal.public.certificates = (response.data.certificates || []).map(cert => ({
              pem: cert.pem, cn: cert.cn, issuer: cert.issuer,
              validFrom: cert.validFrom, validTo: cert.validTo,
              signatureAlgorithm: cert.signatureAlgorithm,
              serialNumber: cert.serialNumber, subject: cert.subject
            }));
            $scope.agent.securityConfig.internal.privateKey = response.data.privateKey;
            $scope.isValidInternalURL = true;
            $scope.internalHostChanged = false;
            $scope.originalInternalHost = $scope.agent.securityConfig.internal.host;
            $scope.keyGenerated = true;
          }
        })
        .catch(function (error) {
          dialogService.showErrorDialog(error.data?.error, error.data?.message);
          console.error('Certificate chain generation failed:', error);
        });
    };

    // ─────────────────────────────────────────────
    // 🔹 Descarga del chain interno
    // ─────────────────────────────────────────────
    $scope.downloadChainInternal = function () {
      const internalCertificates = $scope.agent.securityConfig.internal.public.certificates;
      const privateKey = $scope.agent.securityConfig.internal.privateKey;
      if (!internalCertificates || internalCertificates.length === 0 || !privateKey) {
        console.error('Certificados o clave privada no encontrados.');
        return;
      }
      _triggerDownload(_buildPemContent($scope.agent, internalCertificates, privateKey),
        `bizmetry-${$scope.agent.agentName}-internal.pem`);
    };

    // ─────────────────────────────────────────────
    // 🔹 Descarga del chain externo
    // ─────────────────────────────────────────────
    $scope.downloadChainExternal = function () {
      const externalCertificates = $scope.agent.securityConfig.external.public.certificates;
      const privateKey = $scope.agent.securityConfig.external.privateKey;
      if (!externalCertificates || externalCertificates.length === 0 || !privateKey) {
        console.error('Certificados o clave privada no encontrados.');
        return;
      }
      _triggerDownload(_buildPemContent($scope.agent, externalCertificates, privateKey),
        `bizmetry-${$scope.agent.agentName}-external.pem`);
    };

    // ─────────────────────────────────────────────
    // 🔹 Helpers privados de descarga
    // ─────────────────────────────────────────────
    function _buildPemContent(ag, certificates, privateKey) {
      const ctx = {
        id: ag.agentId || 'N/A',
        agentName: ag.agentName || 'N/A',
        profileId: ag.profileId || 'N/A',
        environmentId: ag.environmentId || 'N/A',
        profileName: ag.profileName || 'N/A',
        environmentName: ag.environmentName || 'N/A',
        accountId: ag.accountId || 'N/A',
        timestamp: new Date().toISOString()
      };
      let content = '';
      content += `# =============================== BIZMETRY INFO ===============================\n`;
      content += `# ID: ${ctx.id}\n# Agent Name: ${ctx.agentName}\n# Profile ID: ${ctx.profileId}\n`;
      content += `# Environment ID: ${ctx.environmentId}\n# Profile Name: ${ctx.profileName}\n`;
      content += `# Environment Name: ${ctx.environmentName}\n# Account ID: ${ctx.accountId}\n`;
      content += `# Exported at: ${ctx.timestamp}\n`;
      content += `# ============================================================================\n\n`;
      certificates.forEach(function (cert) {
        if (!cert.pem) return;
        content += `# ============================== CERTIFICATE ================================\n`;
        content += `# CN: ${cert.cn || 'N/A'}\n# Issuer: ${cert.issuer || 'N/A'}\n`;
        content += `# Valid From: ${cert.validFrom || 'N/A'}\n# Valid To: ${cert.validTo || 'N/A'}\n`;
        content += `# Serial Number: ${cert.serialNumber || 'N/A'}\n`;
        content += `# Signature Algorithm: ${cert.signatureAlgorithm || 'N/A'}\n`;
        content += `# Subject: ${cert.subject || 'N/A'}\n`;
        content += `# ============================================================================\n\n`;
        content += cert.pem + '\n';
      });
      content += `# ============================== PRIVATE KEY ================================\n`;
      content += `# Private Key:\n` + privateKey + '\n';
      content += `# ============================================================================\n`;
      return content;
    }

    function _triggerDownload(content, filename) {
      const blob = new Blob([content], { type: 'application/x-pem-file' });
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = filename;
      downloadLink.click();
      URL.revokeObjectURL(downloadLink.href);
    }

    // ─────────────────────────────────────────────
    // 🔹 Copy helpers (Security tabs)
    // ─────────────────────────────────────────────
    $scope.copyFromInternalGateway = function () {
      $scope.agent.securityConfig.external = $scope.agent.securityConfig.external || {};
      $scope.agent.securityConfig.external.host = $scope.agent.securityConfig.internal.host || 'N/A';
      $scope.validateExternalAgentGwUrl();
    };

    $scope.copyFromExternalGateway = function () {
      $scope.agent.securityConfig.internal = $scope.agent.securityConfig.internal || {};
      $scope.agent.securityConfig.internal.host = $scope.agent.securityConfig.external.host || 'N/A';
      $scope.onInternalHostChange();
    };

    // ─────────────────────────────────────────────
    // 🔹 [MONITORING] Helpers visuales
    // ─────────────────────────────────────────────
    $scope.getThresholdColor = function (valueSet, max) {
      if (!max || max === 0) return '#4caf50';
      const pct = (valueSet / max) * 100;
      if (pct < 60) return '#4caf50';
      if (pct < 80) return '#ff9800';
      return '#f44336';
    };

    $scope.getLatencyBarPos = function (value) {
      const max = $scope.limits.maxLatency;
      if (!max || max === 0) return 0;
      return Math.min(100, (value / max) * 100);
    };

    $scope.getAggregationBarWidth = function (value) {
      const max = $scope.limits.maxAggregationInterval;
      if (!max || max === 0) return 0;
      return Math.min(100, (value / max) * 100);
    };

    // ─────────────────────────────────────────────
    // 🔹 Logging
    // ─────────────────────────────────────────────
    $scope.loggingLevels = ['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR', 'FATAL', 'OFF', 'ALL'];
    if (!$scope.agent || !$scope.agent.agentLogLevel) {
      $scope.agent = $scope.agent || {};
      $scope.agent.agentLogLevel = 'INFO';
      $scope.originalAgent.agentLogLevel = 'INFO';
    }

    // Angular Material 1.2.2's md-select centers its options panel vertically
    // around the selected md-option (see calculateMenuPositions in select.js)
    // rather than opening flush below the trigger — e.g. with 8 Logging Level
    // options, a selection mid-list (the default 'INFO') makes the panel
    // float up and overlap the trigger/label above it; same story for Ingress
    // Type once a non-first option is selected. Same fix already used for the
    // "Show metrics"/"SLA thresholds" dropdowns in AgentStatsDialogController.js,
    // generalized here into one factory since this dialog now has 2 affected
    // selects: remember the trigger on mousedown (fires before Material's own
    // click-driven open logic), then watch for the panel it creates (appended
    // under <body> per Angular Material's `parent: 'body'`, identified by its
    // own `md-container-class`) and force it flush below that trigger the
    // instant Material writes its own top/left/transform.
    function setupDropdownPositioning(panelClassName) {
      var trigger = null;

      function reposition(panelNode) {
        if (!trigger) return;
        var triggerRect = trigger.getBoundingClientRect();
        var desiredLeft = triggerRect.left;
        var maxLeft = window.innerWidth - panelNode.offsetWidth - 8;
        if (maxLeft > 0 && desiredLeft > maxLeft) desiredLeft = maxLeft;

        panelNode.style.transform = 'none';
        panelNode.style.top = (triggerRect.bottom + 4) + 'px';
        panelNode.style.left = Math.max(8, desiredLeft) + 'px';
      }

      if (typeof MutationObserver !== 'undefined') {
        var observer = new MutationObserver(function (mutations) {
          mutations.forEach(function (mutation) {
            Array.prototype.forEach.call(mutation.addedNodes || [], function (node) {
              if (node.nodeType !== 1) return;
              if (!node.classList || !node.classList.contains(panelClassName)) return;

              var settled = false;
              var styleObserver = new MutationObserver(function () {
                if (settled) return;
                settled = true;
                styleObserver.disconnect();
                reposition(node);
              });
              styleObserver.observe(node, { attributes: true, attributeFilter: ['style'] });

              // Fallback in case Material's positioning promise resolves
              // without ever firing a style mutation this session sees.
              $timeout(function () {
                if (settled) return;
                settled = true;
                styleObserver.disconnect();
                reposition(node);
              }, 100);
            });
          });
        });
        observer.observe(document.body, { childList: true });

        $scope.$on('$destroy', function () {
          observer.disconnect();
        });
      }

      return {
        rememberTrigger: function ($event) {
          trigger = $event.currentTarget;
        }
      };
    }

    $scope.rememberLogLevelDropdownTrigger =
      setupDropdownPositioning('agent-log-level-dropdown-panel').rememberTrigger;
    $scope.rememberIngressTypeDropdownTrigger =
      setupDropdownPositioning('agent-ingress-type-dropdown-panel').rememberTrigger;
    $scope.rememberArchitectureDropdownTrigger =
      setupDropdownPositioning('agent-architecture-dropdown-panel').rememberTrigger;

    // ─────────────────────────────────────────────
    // 🔹 [MONITORING] Clamp para threshold dual (CLEAR / SET)
    //    Respeta minRange entre los dos handles y sincroniza rzSlider
    // ─────────────────────────────────────────────
    $scope.clampThreshold = function (metric, edited) {
      var cfg = $scope.agent.monitoringConfig[metric];
      var opts = $scope.sliderOptions[metric].threshold;
      var floor = opts.floor || 0;
      var ceil = opts.ceil;
      var metricKey = 'thresholdMinRange' + metric.charAt(0).toUpperCase() + metric.slice(1);
      var minRange = Math.max(1, $scope.limits[metricKey] || 0);

      var clear = parseFloat(cfg.thresholdClear);
      var set = parseFloat(cfg.thresholdSet);
      if (isNaN(clear)) clear = floor;
      if (isNaN(set)) set = ceil;

      clear = Math.max(floor, Math.min(clear, ceil));
      set = Math.max(floor, Math.min(set, ceil));

      if (edited === 'set') {
        if (set - clear < minRange) { set = clear + minRange; if (set > ceil) set = ceil; }
      } else {
        if (set - clear < minRange) { clear = set - minRange; if (clear < floor) clear = floor; }
      }

      cfg.thresholdClear = clear;
      cfg.thresholdSet = set;
      $timeout(function () { $scope.$broadcast('rzSliderForceRender'); }, 0);
    };

    // ─────────────────────────────────────────────
    // 🔹 Strip port helper (ip-geolocation)
    // ─────────────────────────────────────────────
    $scope.stripPort = function (host) {
      if (!host) return null;
      var lastColon = host.lastIndexOf(':');
      if (lastColon !== -1 && /^\d+$/.test(host.substring(lastColon + 1))) {
        return host.substring(0, lastColon);
      }
      return host;
    };
    // ─────────────────────────────────────────────
    // 🔹 [ALERTING] User search — client-side
    // ─────────────────────────────────────────────
    $scope.alertUserSearch = {
      query: '', displayList: [], loading: false, allUsers: []
    };

    /**
     * Aplica el filtro de elegibles + búsqueda de texto en un solo paso (mismas reglas que el
     * picker de destinatarios de los interceptores):
     *   - sólo usuarios confirmados (completaron el registro);
     *   - si alertEnableSMS está ON → sólo usuarios con user2FANumber cargado.
     * Un destinatario guardado que ya no está en la cuenta (p. ej. borrado) se lista igual, para
     * poder destildarlo.
     * Nota de campos: user2FANumber viene de la API de usuarios;
     *                 mobile2FANumber es el nombre en el modelo AlertUser del agente.
     */
    function _smsOn() {
      return !!($scope.agent.monitoringConfig.alertConfiguration.alertEnableSMS);
    }

    function _has2FA(number) { return !!(number && number.trim()); }

    // Flag habilitado actual de un destinatario — el del usuario recién cargado de la cuenta si se
    // conoce (el backend también lo sincroniza vía UserChangedEvent). Un destinatario deshabilitado
    // queda tildado, pero no recibe alertas mientras esté deshabilitado.
    function _recipientEnabled(recipient) {
      var known = ($scope.alertUserSearch.allUsers || []).filter(function (u) { return u.id === recipient.id; })[0];
      var enabled = known ? known.userEnabled : recipient.userEnabled;
      return enabled != null ? enabled : null;
    }

    // 2FA activo: el flag de la cuenta (user2FAEnabled) y un número registrado.
    function _userHas2FA(u) { return u.user2FAEnabled !== false && _has2FA(u.user2FANumber); }

    function _eligible(u) {
      return u.isConfirmed === true && (!_smsOn() || _userHas2FA(u));
    }

    function _applyAlertUserFilter() {
      var query = ($scope.alertUserSearch.query || '').trim().toLowerCase();
      var known = {};
      $scope.alertUserSearch.allUsers.forEach(function (u) { known[u.id] = true; });
      var candidates = $scope.alertUserSearch.allUsers.filter(_eligible).concat(
        ($scope.agent.monitoringConfig.alertConfiguration.alertUsers || [])
          .filter(function (r) { return !known[r.id]; })
          .map(function (r) { return { id: r.id, email: r.email, user2FANumber: r.mobile2FANumber }; }));

      var filtered = candidates.filter(function (u) {
        // ── Filtro texto ───────────────────────────────────────────────────────
        if (!query) return true;
        return (u.email && u.email.toLowerCase().indexOf(query) !== -1) ||
          (u.firstName && u.firstName.toLowerCase().indexOf(query) !== -1) ||
          (u.lastName && u.lastName.toLowerCase().indexOf(query) !== -1);
      });

      // Selected recipients float to the top so it's clear at a glance who's picked.
      filtered.sort(function (a, b) {
        var aAdded = $scope.isAlertUserAdded(a.id), bAdded = $scope.isAlertUserAdded(b.id);
        if (aAdded !== bAdded) return aAdded ? -1 : 1;
        return (a.email || '').localeCompare(b.email || '');
      });

      $scope.alertUserSearch.displayList = filtered;
    }

    /**
     * Purga de alertUsers los que dejaron de ser elegibles (no confirmados, o sin user2FANumber
     * con SMS ON). Uno que ya no está en la cuenta se conserva, salvo que SMS necesite número.
     * Solo se ejecuta cuando allUsers ya está cargado.
     */
    function _purgeIneligibleRecipients() {
      if (!$scope.alertUserSearch.allUsers.length) return;
      var byId = {};
      $scope.alertUserSearch.allUsers.forEach(function (u) { byId[u.id] = u; });
      $scope.agent.monitoringConfig.alertConfiguration.alertUsers =
        $scope.agent.monitoringConfig.alertConfiguration.alertUsers.filter(function (r) {
          return byId[r.id] ? _eligible(byId[r.id]) : (!_smsOn() || _has2FA(r.mobile2FANumber));
        });
    }

    $scope.alertUserSearch.loading = true;
    UserService.getAllUsers($scope.agent.accountId, { page: 0, size: 1000 })
      .then(function (response) {
        var users = (response.data && response.data.content)
          ? response.data.content
          : (response.data || []);
        $scope.alertUserSearch.allUsers = users;

        // ── Purgar ahora que allUsers está disponible (el $watch inicial lo saltó
        //    por estar vacío): no confirmados, y sin número si SMS ya estaba ON
        _purgeIneligibleRecipients();

        _applyAlertUserFilter();
      })
      .catch(function (err) {
        console.error('❌ Error loading users:', err);
      })
      .finally(function () {
        $scope.alertUserSearch.loading = false;
      });

    $scope.onAlertUserSearchInput = function () {
      _applyAlertUserFilter();
    };

    // Re-filtrar al activar/desactivar SMS — también purga de alertUsers
    // los que ya fueron agregados pero no tienen user2FANumber.
    // CRÍTICO: no purgar si allUsers todavía no cargó (race condition al abrir
    // el diálogo con alertEnableSMS ya en true — en ese caso la purga la
    // ejecuta el .then() del UserService una vez que tiene los datos reales).
    $scope.$watch('agent.monitoringConfig.alertConfiguration.alertEnableSMS', function (smsEnabled) {
      if (smsEnabled && $scope.alertUserSearch.allUsers.length > 0) {
        _purgeIneligibleRecipients();
      }
      _applyAlertUserFilter();
    });

    $scope.isAlertUserAdded = function (userId) {
      return ($scope.agent.monitoringConfig.alertConfiguration.alertUsers || [])
        .some(function (u) { return u.id === userId; });
    };

    $scope.toggleAlertUser = function (user) {
      console.log('USER toggled->', user);
      var users = $scope.agent.monitoringConfig.alertConfiguration.alertUsers;
      var idx = users.findIndex(function (u) { return u.id === user.id; });
      if (idx === -1) {
        users.push({
          id: user.id,
          email: user.email,
          mobile2FANumber: user.user2FANumber || null,  // user2FANumber (API users) → mobile2FANumber (AlertUser)
          userEnabled: user.userEnabled != null ? user.userEnabled : null
        });
      } else {
        users.splice(idx, 1);
      }
    };

    // ─────────────────────────────────────────────
// 🔹 [ALERTING] Mask 2FA number helper
// ─────────────────────────────────────────────
$scope.mask2FA = function (number) {
  if (!number || !number.trim()) return null;
  var clean = number.trim();
  return '****' + clean.slice(-4);
};
  }

  

]);